import type {
  BookingRulesSettings,
  PhoneRevealSummary,
  StaffVisibilityView,
  UpdateStaffVisibilityRequest,
  BranchGridPreview,
  BranchSettingsView,
  UpdateBookingRulesRequest,
  UpsertBranchRequest,
} from "@planerius/contracts/cabinet"

import { apiRequest } from "./http"

/**
 * Настройки салона: правила записи (4-Б4).
 *
 * Ответ разобран сервером: «правил нет вовсе» (действуют умолчания платформы) и «правила
 * настроены ровно так же» — разные состояния, и экран обязан их различать.
 */

export function fetchBookingRules(signal?: AbortSignal): Promise<BookingRulesSettings> {
  return apiRequest("/cabinet/settings/booking-rules", { signal })
}

/**
 * Сохранение. Версия строки обязательна: две открытые вкладки настроек иначе затирают
 * друг друга молча, а правила записи — семь взаимосвязанных чисел.
 */
export function saveBookingRules(request: UpdateBookingRulesRequest): Promise<{ readonly ok: boolean }> {
  return apiRequest("/cabinet/settings/booking-rules", { method: "POST", body: request })
}

/**
 * Филиалы салона со всеми полями и версией строки (4-Б1).
 *
 * Ответ типизирован: раньше экран собирал поля наугад и стирал те, которых не знал, —
 * координаты и телефон правят раз в жизни, и именно они пропадали.
 */
export function fetchBranches(signal?: AbortSignal): Promise<readonly BranchSettingsView[]> {
  return apiRequest("/cabinet/settings/branches", { signal })
}

/**
 * Сколько будущих записей окажется вне новой сетки. Спрашивается ДО сохранения:
 * после него это уже не решение, а объяснение случившегося.
 */
export function fetchGridPreview(
  branchId: string,
  slotStepMinutes: number,
  signal?: AbortSignal,
): Promise<BranchGridPreview> {
  return apiRequest(`/cabinet/settings/branches/${branchId}/grid-preview`, {
    query: { slotStepMinutes: String(slotStepMinutes) },
    signal,
  })
}

/**
 * Сохранение филиала. Запрос несёт ВСЕ поля прочитанного филиала, а не только видимые
 * на экране: это upsert, и пропущенное он обнулит.
 */
export function saveBranch(request: UpsertBranchRequest): Promise<{ readonly id: string }> {
  return apiRequest("/cabinet/settings/branches", { method: "POST", body: request })
}

/**
 * Что мастер видит о клиенте (4-Б12) — с версией строки: две открытые вкладки иначе
 * затирают друг друга молча, а цена молчания здесь — открытая база клиентов.
 */
export function fetchStaffVisibilitySettings(signal?: AbortSignal): Promise<StaffVisibilityView> {
  return apiRequest("/cabinet/settings/staff-visibility", { signal })
}

export function saveStaffVisibility(
  request: UpdateStaffVisibilityRequest,
): Promise<StaffVisibilityView> {
  return apiRequest("/cabinet/settings/staff-visibility", { method: "POST", body: request })
}

/**
 * Журнал открытий телефона: кто, сколько раз и скольких РАЗНЫХ клиентов. Разница между
 * этими двумя числами и есть предмет разговора о доверии.
 */
export function fetchPhoneReveals(days = 7, signal?: AbortSignal): Promise<PhoneRevealSummary> {
  return apiRequest("/cabinet/settings/phone-reveals", { query: { days: String(days) }, signal })
}
